/**
 * The receive mixer (web-plan.md F4): three voice slots and the share-audio
 * lane, each through its own GainNode so a per-person volume (0..2, D33's
 * range) lands on whatever slot that person currently occupies.
 *
 * Chrome quirk that shapes the plumbing: a remote MediaStreamTrack feeds a
 * MediaStreamAudioSourceNode only while it is ALSO sinked somewhere, so each
 * slot keeps a muted `<audio>` element whose only job is to make the track
 * flow. The audible path is WebAudio's.
 *
 * After the master sits the loudness stage (D340): the +6 dB the desktop's
 * mixer adds, held under the ceiling by a compressor at a limiter's ratio, so
 * «100 %» means about the same in both clients. Receive side only — the
 * browser's own AGC levels the microphone on the way out.
 */
export const VOICE_SLOTS = 3;
/** The share-audio lane's slot index, after the voice slots (F5 plays it). */
export const SHARE_AUDIO_SLOT = VOICE_SLOTS;

/** The makeup gain, +6 dB — `core/src/loudness.rs`'s `MAKEUP`. */
const MAKEUP = 2;

interface Lane {
  el: HTMLAudioElement;
  gain: GainNode;
}

export class Mixer {
  private ctx: AudioContext;
  private master: GainNode;
  private lanes: Record<number, Lane> = {};

  constructor() {
    this.ctx = new AudioContext({ sampleRate: 48_000 });
    this.master = this.ctx.createGain();
    const makeup = this.ctx.createGain();
    makeup.gain.value = MAKEUP;
    // A compressor at a high ratio is the browser's limiter: a peak past the
    // makeup is held back rather than clipped at the destination. Its
    // threshold sits near the top on purpose — the Web Audio compressor adds
    // a makeup gain of its own, derived from how hard its curve bites at full
    // scale, and a low threshold would have it bite early and add several dB
    // on top of ours. Measured in Chromium (an OfflineAudioContext, one
    // second of tone per case): a quiet tone comes out +7.7 dB, and the
    // loudest case tried — a full-scale 40 Hz sine, doubled — peaks at
    // −0.4 dBFS. A 3 dB knee or a 100 ms release let that one through at
    // +0.05.
    const limiter = this.ctx.createDynamicsCompressor();
    limiter.threshold.value = -3;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    this.master.connect(makeup);
    makeup.connect(limiter);
    limiter.connect(this.ctx.destination);
  }

  /** Autoplay policy: called from the join click, the one blessed gesture. */
  async resume(): Promise<void> {
    if (this.ctx.state !== "running") await this.ctx.resume().catch(() => {});
  }

  attach(slot: number, track: MediaStreamTrack): void {
    const stream = new MediaStream([track]);
    const el = new Audio();
    el.srcObject = stream;
    el.muted = true;
    void el.play().catch(() => {});
    const gain = this.ctx.createGain();
    this.ctx.createMediaStreamSource(stream).connect(gain);
    gain.connect(this.master);
    this.lanes[slot] = { el, gain };
  }

  setSlotGain(slot: number, value: number): void {
    const lane = this.lanes[slot];
    if (lane) lane.gain.gain.value = value;
  }

  /** Deafen is the master, not per-lane: one decision, one node. */
  setDeafened(deafened: boolean): void {
    this.master.gain.value = deafened ? 0 : 1;
  }

  close(): void {
    for (const lane of Object.values(this.lanes)) {
      lane.el.pause();
      lane.el.srcObject = null;
    }
    this.lanes = {};
    void this.ctx.close().catch(() => {});
  }
}
